/* eslint-disable max-len */
import { Box, Divider, Grid2, IconButton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { chartsGridClasses } from "@mui/x-charts/ChartsGrid";
import { areaElementClasses, LineChart } from "@mui/x-charts/LineChart";
import { AxisId } from "@mui/x-charts/models/axis";
import moment from "moment";
import * as React from "react";
import { useEffect, useState, useId, useRef } from "react";
import { createPortal } from "react-dom";
import ApiRoutes from "../../config/api.routes";
import { useAxios } from "../../services/http.service";
import { formatValue } from "../../utils/formatValue";
import { numberWithCommas, toReadable, sixDigits } from "../../utils/methods";
import { convertParcelsToHectares } from "../../pages/EcoBank";
import { HALVING_THRESHOLDS } from "../../utils/constants";
import { EDITION_BADGES, EditionBadge } from "../../utils/editionBadges";

interface GridDemoProps {
  onClose: () => void;
  type: string;
}

// MUI `Modal` clones its child to attach a ref (and a `tabIndex` for focus
// trapping), so a plain function component with a Fragment root can't be used
// directly. `PopUpGraph` is exported as a forwardRef that lands both on the
// outer Box — see the wrapper at the bottom of this file.
type PopUpGraphDomProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onClose" | "type">;

export function AreaGradient({ color, id }: { color: string; id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%" gradientUnits="objectBoundingBox">
        <stop offset="0%" stopColor={color} stopOpacity={0.6} />
        <stop offset="100%" stopColor={color} stopOpacity={0.0} />
      </linearGradient>
    </defs>
  );
}

export const formatWithPrefix = (value: number, decimalPlaces = 2, currency = ""): string => {
  if (isNaN(value) || value === null) {
    return "N/A";
  }
  if (value === 0) {
    return currency ? `${currency}0` : "0";
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const currencyPrefix = currency ? `${currency}` : "";

  if (absValue >= 1e9) {
    const formatted = (absValue / 1e9).toFixed(decimalPlaces);
    return `${sign}${currencyPrefix}${parseFloat(formatted)}B`;
  } else if (absValue >= 1e6) {
    const formatted = (absValue / 1e6).toFixed(decimalPlaces);
    return `${sign}${currencyPrefix}${parseFloat(formatted)}M`;
  } else if (absValue >= 1e3) {
    const formatted = (absValue / 1e3).toFixed(decimalPlaces);
    return `${sign}${currencyPrefix}${parseFloat(formatted)}K`;
  } else {
    const formatted = absValue.toFixed(decimalPlaces);
    return `${sign}${currencyPrefix}${parseFloat(formatted)}`;
  }
};

// ── Dark HUD Tooltip shell ───────────────────────────────────────────────────
const CHART_BLUE = "#38bdf8";
const KHAKI = "#9AA080";

const hudTooltipSx = {
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "start",
  backgroundColor: "rgba(14, 16, 14, 0.96)",
  borderRadius: "8px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.8), 0 0 20px rgba(56,189,248,0.15)",
  border: `1px solid rgba(56,189,248,0.4)`,
  padding: "16px 20px",
  gap: 1,
  backdropFilter: "blur(12px)",
  minWidth: "240px",
};

const HudLabel = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Typography
    fontFamily="'IBM Plex Mono', monospace"
    fontWeight={700}
    fontSize="10px"
    letterSpacing="0.08em"
    textTransform="uppercase"
    color={KHAKI}
    sx={sx}
  >
    {children}
  </Typography>
);

const HudValue = ({ children }: { children: React.ReactNode }) => (
  <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={700} fontSize="15px" color="#FFFFFF">
    {children}
  </Typography>
);

const HudRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
    <HudLabel>{label}</HudLabel>
    <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={700} fontSize="11px" color="#FFFFFF">
      {value}
    </Typography>
  </Box>
);

// ── Time-filter pill ─────────────────────────────────────────────────────────
const Pill = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      padding: "6px 16px",
      borderRadius: "8px",
      fontSize: "11px",
      fontWeight: 700,
      fontFamily: "'IBM Plex Mono', monospace",
      letterSpacing: "0.05em",
      textTransform: "uppercase",
      cursor: "pointer",
      border: "1px solid",
      transition: "all 0.2s",
      color: active ? CHART_BLUE : KHAKI,
      backgroundColor: active ? "rgba(56,189,248,0.15)" : "rgba(255,255,255,0.04)",
      borderColor: active ? CHART_BLUE : "transparent",
      boxShadow: active ? "0 0 10px rgba(56,189,248,0.2)" : "none",
      "&:hover": {
        color: active ? CHART_BLUE : "#FFFFFF",
        backgroundColor: active ? "rgba(56,189,248,0.15)" : "rgba(255,255,255,0.08)",
      },
    }}
  >
    {label}
  </Box>
);

// ── Shared chart sx ──────────────────────────────────────────────────────────
const buildChartSx = (gradientId: string) => ({
  height: "85%",
  [`& .${chartsGridClasses.line}`]: {
    stroke: "rgba(255,255,255,0.05)",
    strokeOpacity: 1,
    strokeWidth: 1,
    strokeDasharray: "none !important",
  },
  "& .MuiChartsAxis-root.MuiChartsAxis-directionX.MuiChartsAxis-bottom": {
    transform: "translate(0px,280px)",
  },
  "& .css-wke9ls-MuiChartsAxis-root-MuiChartsYAxis-root": {
    transform: "translateX(50px)",
  },
  [`& .${areaElementClasses.root}`]: {
    fill: `url(#${gradientId}) !important`,
    fillOpacity: 1,
  },
  "& .MuiChartsAxis-label": {
    transform: "translate(-20px, 10px)",
    fill: `${KHAKI} !important`,
  },
  "& .MuiChartsAxis-tickLabel": {
    fill: `${KHAKI} !important`,
    fontFamily: "'IBM Plex Mono', monospace !important",
    fontWeight: "600 !important",
    fontSize: "11px !important",
    letterSpacing: "0.05em",
  },
  // Glow on the line
  "& .MuiLineElement-root": {
    stroke: `${CHART_BLUE} !important`,
    strokeWidth: "2.5px !important",
    filter: `drop-shadow(0px 4px 12px rgba(56,189,248,0.5))`,
  },
});

// ── 3D Gem canvas helpers ────────────────────────────────────────────────────
const LX = -0.5,
  LY = -0.8,
  LZ = 0.6,
  LL = Math.sqrt(LX * LX + LY * LY + LZ * LZ);
const GEM_LIGHT = { x: LX / LL, y: LY / LL, z: LZ / LL };

function hexToRgb(h: string): [number, number, number] {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
}
function applyLight(rgb: [number, number, number], f: number): [number, number, number] {
  return rgb.map((c) => Math.min(255, Math.max(0, Math.round(c * f)))) as [number, number, number];
}
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs(((h / 60) % 2) - 1)),
    m = l - c / 2;
  let r = 0,
    g = 0,
    b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}
function gemFaceNormal(p0: any, p1: any, p2: any) {
  const abx = p1.x - p0.x,
    aby = p1.y - p0.y,
    abz = p1.z - p0.z;
  const acx = p2.x - p0.x,
    acy = p2.y - p0.y,
    acz = p2.z - p0.z;
  const nx = aby * acz - abz * acy,
    ny = abz * acx - abx * acz,
    nz = abx * acy - aby * acx;
  const l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  return { x: nx / l, y: ny / l, z: nz / l };
}
function drawGemFrame(canvas: HTMLCanvasElement, badge: EditionBadge, rotY: number, hueShift: number) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const W = canvas.width,
    H = canvas.height,
    cx = W / 2,
    cy = H / 2,
    R = W / 2 - 0.5;
  ctx.clearRect(0, 0, W, H);
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.clip();

  ctx.fillStyle = badge.active ? "#A855F7" : "rgba(255,255,255,0.05)";
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = badge.active ? badge.activeBg : "#060708";
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.92, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = badge.base;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.84, 0, Math.PI * 2);
  ctx.fill();

  const gr = 0.44,
    dh = 0.58,
    FOV = 2.6,
    sc = R * 0.78;
  const G = [0, 1.57, 3.14, 4.71].map((a) => ({ x: Math.cos(a + rotY) * gr, y: 0, z: Math.sin(a + rotY) * gr }));
  const TP = { x: 0, y: -dh, z: 0 },
    BP = { x: 0, y: dh, z: 0 };
  const proj = (p: any) => ({ x: cx + (p.x / (p.z + FOV)) * FOV * sc, y: cy + (p.y / (p.z + FOV)) * FOV * sc });
  const GP = G.map(proj),
    TPP = proj(TP),
    BPP = proj(BP);

  const faces: any[] = [];
  for (let i = 0; i < 4; i++) {
    const n = (i + 1) % 4;
    const cn = gemFaceNormal(TP, G[i], G[n]);
    const cl = Math.max(0, cn.x * GEM_LIGHT.x + cn.y * GEM_LIGHT.y + cn.z * GEM_LIGHT.z);
    faces.push({
      v: [TPP, GP[i], GP[n]],
      v3: [TP, G[i], G[n]],
      z: (G[i].z + G[n].z) / 2,
      idx: i,
      crown: true,
      light: cl,
    });
    const pn = gemFaceNormal(BP, G[n], G[i]);
    const pl = Math.max(0, pn.x * GEM_LIGHT.x + pn.y * GEM_LIGHT.y + pn.z * GEM_LIGHT.z);
    faces.push({
      v: [BPP, GP[n], GP[i]],
      v3: [BP, G[n], G[i]],
      z: (G[i].z + G[n].z) / 2,
      idx: i,
      crown: false,
      light: pl,
    });
  }
  faces
    .sort((a, b) => a.z - b.z)
    .forEach((f) => {
      if (gemFaceNormal(f.v3[0], f.v3[1], f.v3[2]).z <= 0) return;
      let rgb: [number, number, number];
      if (badge.prismatic) {
        const baseHue = ([45, 150, 270, 330][f.idx] + hueShift) % 360;
        const baseLightness = f.crown ? (f.idx % 2 ? 85 : 65) : f.idx % 2 ? 45 : 30;
        rgb = hslToRgb(baseHue, 95, baseLightness);
      } else {
        const col = f.crown ? (f.idx % 2 ? badge.f1 : badge.f2) : f.idx % 2 ? badge.f3 : badge.f4;
        rgb = hexToRgb(col);
      }
      const lit = applyLight(rgb, 0.4 + f.light * 1.2);
      ctx.fillStyle = `rgb(${lit[0]},${lit[1]},${lit[2]})`;
      ctx.beginPath();
      ctx.moveTo(f.v[0].x, f.v[0].y);
      f.v.forEach((p: any) => ctx.lineTo(p.x, p.y));
      ctx.fill();
    });
  ctx.restore();
}

// ── Gem canvas component ─────────────────────────────────────────────────────
function GemCanvas({ badge }: { badge: EditionBadge }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const t0Ref = useRef<number | null>(null);

  useEffect(() => {
    const rotPeriod = badge.active ? badge.pulse * 1800 : 12000;
    const loop = (ts: number) => {
      if (!t0Ref.current) t0Ref.current = ts;
      const elapsed = (ts - t0Ref.current) / 1000;
      const rot = (((elapsed * 1000) / rotPeriod) * Math.PI * 2) % (Math.PI * 2);
      if (canvasRef.current) drawGemFrame(canvasRef.current, badge, rot, elapsed * 15);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [badge]);

  return (
    <canvas
      ref={canvasRef}
      width={56}
      height={56}
      style={{ width: 56, height: 56, borderRadius: "50%", display: "block" }}
    />
  );
}

// ── Halving Matrix Panel ─────────────────────────────────────────────────────
const EARTH_TOTAL_HA = 14_890_000_000;
const PLATFORM_TARGET_HA_YEARS = 9_000_000_000;

interface HalvingMatrixPanelProps {
  currentEdition: number;
  activeHaYears: number;
  mobile: boolean;
}

function HalvingMatrixPanel({ currentEdition, activeHaYears, mobile }: HalvingMatrixPanelProps) {
  const [viewIdx, setViewIdx] = useState(currentEdition - 1);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; blockIdx: number; active: boolean } | null>(null);

  // currentEdition arrives async (indicators fetch). It defaults to 1 on first mount,
  // so the carousel would otherwise open stuck on Edition 1; snap it to the live edition
  // once the real value resolves. currentEdition is stable afterwards, so this won't
  // fight the user's PREV/NEXT navigation.
  useEffect(() => {
    setViewIdx(currentEdition - 1);
  }, [currentEdition]);

  const badge = { ...EDITION_BADGES[viewIdx], active: viewIdx === currentEdition - 1 };

  const isComplete = viewIdx < currentEdition - 1;
  const isLocked = viewIdx > currentEdition - 1;

  const editionThresholdHA = convertParcelsToHectares(HALVING_THRESHOLDS[viewIdx]);
  const prevThresholdHA = viewIdx > 0 ? convertParcelsToHectares(HALVING_THRESHOLDS[viewIdx - 1]) : 0;
  const editionSpanHA = editionThresholdHA - prevThresholdHA;
  const blockSizeHA = editionSpanHA / 100;

  let editionActiveHA: number;
  if (isComplete) {
    editionActiveHA = editionSpanHA;
  } else if (isLocked) {
    editionActiveHA = 0;
  } else {
    editionActiveHA = Math.min(activeHaYears - prevThresholdHA, editionSpanHA);
    editionActiveHA = Math.max(0, editionActiveHA);
  }

  const activeBlocks = Math.min(100, Math.floor((editionActiveHA / editionSpanHA) * 100));
  const fillPct = editionSpanHA > 0 ? ((editionActiveHA / editionSpanHA) * 100).toFixed(1) : "0.0";

  const totalProtocolHA =
    EDITION_BADGES.slice(0, currentEdition - 1).reduce((sum, _b, i) => {
      const prev = i > 0 ? convertParcelsToHectares(HALVING_THRESHOLDS[i - 1]) : 0;
      return sum + (convertParcelsToHectares(HALVING_THRESHOLDS[i]) - prev);
    }, 0) + editionActiveHA;
  const earthPct = ((totalProtocolHA / EARTH_TOTAL_HA) * 100).toFixed(4);
  const platformPct = Math.min(100, (totalProtocolHA / PLATFORM_TARGET_HA_YEARS) * 100);

  const poolStatus = isComplete ? "COMPLETED" : isLocked ? "🔒 LOCKED" : "ACTIVE";
  const poolStatusColor = isComplete ? "#38bdf8" : isLocked ? "#ef4444" : "#4ade80";

  const tooltipBlockHA = blockSizeHA > 0 ? blockSizeHA : 0;

  const fmt = (n: number) => numberWithCommas(Number(n.toFixed(2)));

  const holoStyle: React.CSSProperties = {
    position: "absolute",
    inset: -10,
    pointerEvents: "none",
    opacity: 0.55,
    mixBlendMode: "color-dodge",
    background:
      "linear-gradient(110deg, transparent 10%, rgba(255,180,200,0.7) 25%, rgba(255,235,150,0.7) 45%, rgba(150,255,210,0.7) 65%, rgba(170,200,255,0.7) 85%, transparent 95%)",
    backgroundSize: "300% 300%",
    filter: "blur(4px)",
    animation: "holo-shimmer 5s infinite linear",
    borderRadius: "50%",
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        px: mobile ? "20px" : "28px",
        pb: mobile ? "12px" : "20px",
        "@keyframes holo-shimmer": {
          "0%": { backgroundPosition: "150% 150%" },
          "100%": { backgroundPosition: "-50% -50%" },
        },
        "@keyframes matrix-scan": {
          "0%": { transform: "translateY(-80px)" },
          "100%": { transform: "translateY(320px)" },
        },
      }}
    >
      {/* Earth impact bar */}
      <Box
        sx={{
          background: "rgba(0,0,0,0.25)",
          border: "1px solid rgba(255,255,255,0.04)",
          borderRadius: "12px",
          p: "12px 14px",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: "8px" }}>
          <Typography
            fontFamily="Inter"
            fontWeight={700}
            fontSize="10px"
            color={KHAKI}
            letterSpacing="0.05em"
            textTransform="uppercase"
          >
            Total Protocol Impact (6.04% Earth Target)
          </Typography>
          <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={700} fontSize="13px" color="#4ade80">
            {earthPct}%
          </Typography>
        </Box>
        <Box
          sx={{
            width: "100%",
            height: "4px",
            background: "rgba(255,255,255,0.08)",
            borderRadius: "2px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${platformPct}%`,
              background: "#4ade80",
              boxShadow: "0 0 8px #4ade80",
              transition: "width 0.5s ease",
            }}
          />
        </Box>
      </Box>

      {/* Edition carousel nav */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "rgba(0,0,0,0.2)",
          border: "1px solid rgba(255,255,255,0.03)",
          borderRadius: "8px",
          p: "10px 16px",
        }}
      >
        <Box
          component="button"
          onClick={() => viewIdx > 0 && setViewIdx(viewIdx - 1)}
          sx={{
            background: "none",
            border: "none",
            cursor: viewIdx > 0 ? "pointer" : "not-allowed",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: "'IBM Plex Mono', monospace",
            letterSpacing: "0.08em",
            color: viewIdx > 0 ? "#4ade80" : "rgba(255,255,255,0.18)",
            transition: "text-shadow 0.2s",
            p: 0,
            "&:hover": viewIdx > 0 ? { textShadow: "0 0 8px #4ade80" } : {},
          }}
        >
          ← PREV POOL
        </Box>
        <Typography
          fontFamily="'IBM Plex Mono', monospace"
          fontWeight={700}
          fontSize="12px"
          color="#FFFFFF"
          letterSpacing="0.05em"
        >
          EDITION {viewIdx + 1}
        </Typography>
        <Box
          component="button"
          onClick={() => viewIdx < 11 && setViewIdx(viewIdx + 1)}
          sx={{
            background: "none",
            border: "none",
            cursor: viewIdx < 11 ? "pointer" : "not-allowed",
            fontSize: "10px",
            fontWeight: 700,
            fontFamily: "'IBM Plex Mono', monospace",
            letterSpacing: "0.08em",
            color: viewIdx < 11 ? "#4ade80" : "rgba(255,255,255,0.18)",
            transition: "text-shadow 0.2s",
            p: 0,
            "&:hover": viewIdx < 11 ? { textShadow: "0 0 8px #4ade80" } : {},
          }}
        >
          NEXT POOL →
        </Box>
      </Box>

      {/* Header: gem + title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
        <Box
          sx={{
            position: "relative",
            width: 56,
            height: 56,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.8)",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <GemCanvas badge={badge} />
          <Box sx={holoStyle} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography fontFamily="Inter" fontWeight={600} fontSize="15px" color="#FFFFFF" letterSpacing="0.01em">
            Active Hectare/Years Matrix
          </Typography>
          <Typography
            fontFamily="'IBM Plex Mono', monospace"
            fontWeight={700}
            fontSize="11px"
            color="#4ade80"
            letterSpacing="0.05em"
            textTransform="uppercase"
          >
            Edition {viewIdx + 1} — {badge.name}
          </Typography>
        </Box>
      </Box>

      {/* Stat cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
        {[
          { val: fmt(editionSpanHA), label: "Target (HA Yrs)" },
          { val: fmt(blockSizeHA), label: "HA Yrs / Block" },
          { val: badge.rate.toFixed(4), label: "Max Mint (TR3/D)" },
        ].map(({ val, label }) => (
          <Box
            key={label}
            sx={{
              background: "rgba(255,255,255,0.01)",
              border: "1px solid rgba(255,255,255,0.03)",
              borderRadius: "12px",
              p: "10px 8px",
              textAlign: "center",
            }}
          >
            <Typography
              fontFamily="'IBM Plex Mono', monospace"
              fontWeight={700}
              fontSize="11px"
              color="#FFFFFF"
              mb="3px"
              noWrap
            >
              {val}
            </Typography>
            <Typography
              fontFamily="Inter"
              fontWeight={600}
              fontSize="9px"
              color={KHAKI}
              letterSpacing="0.01em"
              textTransform="uppercase"
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Matrix block grid */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          border: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(0,0,0,0.3)",
          borderRadius: "12px",
          overflow: "hidden",
          p: "16px 16px 0 16px",
        }}
      >
        {/* scanline */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            height: "80px",
            width: "100%",
            background:
              "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.01) 40%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.01) 60%, transparent 100%)",
            mixBlendMode: "overlay",
            zIndex: 10,
            animation: "matrix-scan 4s cubic-bezier(0.4,0,0.2,1) infinite",
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(10, 1fr)",
            gap: "4px",
            width: "100%",
            maxWidth: "250px",
            aspectRatio: "1",
            mb: "16px",
            zIndex: 20,
          }}
        >
          {Array.from({ length: 100 }, (_, i) => {
            const isActive = i < activeBlocks;
            const seed = (i * 17 + viewIdx * 11) % 100;
            const alpha = isActive
              ? isComplete
                ? 0.12 + ((82 + ((i * 3) % 19)) / 100) * 0.88
                : 0.12 + (seed < 12 ? 0.33 : seed < 32 ? 0.4 : seed < 68 ? 0.65 : 0.88) * 0.88
              : 0;
            return (
              <Box
                key={i}
                onMouseEnter={(e) => setTooltip({ x: e.clientX, y: e.clientY, blockIdx: i, active: isActive })}
                onMouseMove={(e) => setTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null))}
                onMouseLeave={() => setTooltip(null)}
                sx={{
                  borderRadius: "2px",
                  cursor: "pointer",
                  transition: "transform 0.1s ease, background 0.1s ease",
                  background: isActive ? `rgba(74,222,128,${alpha})` : "rgba(255,255,255,0.03)",
                  border: isActive ? "none" : "1px solid rgba(255,255,255,0.06)",
                  boxShadow:
                    isActive && alpha > 0.7 ? `inset 0 0 4px rgba(134,239,172,${((alpha - 0.7) / 0.3) * 0.4})` : "none",
                  "&:hover": {
                    transform: "scale(1.3)",
                    zIndex: 30,
                    position: "relative",
                    background: isActive ? "#86efac !important" : "rgba(255,255,255,0.08)",
                    boxShadow: isActive ? "0 0 10px rgba(134,239,172,0.8)" : "none",
                    border: isActive ? "none" : "1px solid rgba(255,255,255,0.2)",
                  },
                }}
              />
            );
          })}
        </Box>

        {/* Grid legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: "10px 14px",
            width: "calc(100% + 32px)",
            ml: "-16px",
            background: "rgba(0,0,0,0.4)",
            borderTop: "1px solid rgba(255,255,255,0.04)",
            fontSize: "10px",
            fontWeight: 600,
            zIndex: 20,
          }}
        >
          <Typography
            fontFamily="'IBM Plex Mono', monospace"
            fontWeight={600}
            fontSize="10px"
            color={KHAKI}
            letterSpacing="0.05em"
            textTransform="uppercase"
          >
            Pool Status:{" "}
            <Box component="span" sx={{ color: poolStatusColor }}>
              {poolStatus}
            </Box>
          </Typography>
          <Typography
            fontFamily="'IBM Plex Mono', monospace"
            fontWeight={600}
            fontSize="10px"
            color={KHAKI}
            letterSpacing="0.05em"
            textTransform="uppercase"
          >
            Progress:{" "}
            <Box component="span" sx={{ color: "#4ade80" }}>
              {fillPct}%
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Data list */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          mx: mobile ? "-20px" : "-28px",
        }}
      >
        {[
          {
            dot: "#4ade80",
            dotShadow: "rgba(74,222,128,0.4)",
            label: "Active Hectare/Years",
            val: fmt(editionActiveHA),
          },
          {
            dot: "rgba(255,255,255,0.03)",
            dotBorder: "rgba(255,255,255,0.06)",
            label: "Hectare/Years Till Halving",
            val: fmt(Math.max(0, editionSpanHA - editionActiveHA)),
          },
        ].map(({ dot, dotShadow, dotBorder, label, val }, i) => (
          <Box
            key={label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "14px 28px",
              background: i === 1 ? "rgba(255,255,255,0.01)" : "transparent",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "2px",
                  background: dot,
                  flexShrink: 0,
                  ...(dotShadow && { boxShadow: `0 0 8px ${dotShadow}` }),
                  ...(dotBorder && { border: `1px solid ${dotBorder}` }),
                }}
              />
              <Typography
                fontFamily="Inter"
                fontWeight={600}
                fontSize="11px"
                color="#FFFFFF"
                letterSpacing="0.03em"
                textTransform="uppercase"
              >
                {label}
              </Typography>
            </Box>
            <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={600} fontSize="12px" color="#FFFFFF">
              {val}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Block tooltip portal */}
      {tooltip &&
        createPortal(
          <Box
            sx={{
              position: "fixed",
              left: tooltip.x,
              top: tooltip.y,
              pointerEvents: "none",
              zIndex: 9999,
              transform: "translate(-50%, calc(-100% - 12px))",
              background: "rgba(14,16,14,0.98)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px",
              p: "10px 14px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
              backdropFilter: "blur(10px)",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              fontFamily="Inter"
              fontWeight={700}
              fontSize="9px"
              color={tooltip.active ? "#4ade80" : KHAKI}
              letterSpacing="0.05em"
              textTransform="uppercase"
              mb="4px"
            >
              {tooltip.active
                ? isComplete
                  ? "COMPLETED"
                  : "ACTIVE MINTING"
                : isLocked
                ? "POOL INACTIVE"
                : "OPEN CAPACITY"}
            </Typography>
            <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={600} fontSize="11px" color="#FFFFFF">
              {fmt(tooltipBlockHA)}{" "}
              <Box component="span" sx={{ color: "#4ade80" }}>
                HA Years
              </Box>
            </Typography>
          </Box>,
          document.body
        )}
    </Box>
  );
}

function PopUpGraphInner({
  onClose,
  type,
  forwardedRef,
  ...rest
}: GridDemoProps & PopUpGraphDomProps & { forwardedRef?: React.Ref<HTMLDivElement> }) {
  const axios = useAxios();
  const [charts, setCharts] = useState<{ x: any; y: any }[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const uniqueId = useId();
  const gradientId = `chart-gradient-${uniqueId.replace(/:/g, "")}`;

  const [timeframe, setTimeframe] = React.useState("all");
  const [indicators, setIndicators] = useState<any>(null);
  const isMarketCap = type === "marketCap";
  const isParcels = type === "parcels";
  const isPieChart = isMarketCap || isParcels;

  const getMaxFromChartData = (key: string): number => {
    if (!chartData.length) return 0;
    return chartData.reduce((max, item) => {
      const value = Number(item?.[key] ?? 0);
      return Number.isFinite(value) ? Math.max(max, value) : max;
    }, 0);
  };

  const maxMarketCap = getMaxFromChartData("marketCap");
  const maxSupply = getMaxFromChartData("supply");

  const guardianRedemption =
    indicators?.globalMintGuardian !== undefined
      ? Number(
          toReadable(indicators.globalMintGuardian.toLocaleString("fullwide", { useGrouping: false }), true, false)
        )
      : getMaxFromChartData("guardianRedemption");
  const patronRedemption =
    indicators?.globalMintPatron !== undefined
      ? Number(toReadable(indicators.globalMintPatron.toLocaleString("fullwide", { useGrouping: false }), true, false))
      : getMaxFromChartData("patronRedemption");
  const referralRedemption =
    indicators?.globalMintReferral !== undefined
      ? Number(
          toReadable(indicators.globalMintReferral.toLocaleString("fullwide", { useGrouping: false }), true, false)
        )
      : getMaxFromChartData("referralRedemption");

  const totalRedeemed = guardianRedemption + patronRedemption + referralRedemption;
  const remainingSupply = Math.max(maxSupply - totalRedeemed, 0);

  const marketCapSlices = [
    { id: 0, value: guardianRedemption, label: "Guardian", color: "#A855F7" },
    { id: 1, value: patronRedemption, label: "Patron", color: "#3b82f6" },
    { id: 2, value: referralRedemption, label: "Referral", color: "#FACC15" },
    { id: 3, value: remainingSupply, label: "Unredeemed", color: "#4ade80" },
  ];

  const marketCapPieTotal = marketCapSlices.reduce((sum, s) => sum + (Number.isFinite(s.value) ? s.value : 0), 0);
  const marketCapTooltipTotal = marketCapPieTotal > 0 ? marketCapPieTotal : maxSupply;

  const [hoveredMarketCapSlice, setHoveredMarketCapSlice] = useState<number | null>(null);
  const [mcTooltip, setMcTooltip] = useState<{
    x: number;
    y: number;
    slice: (typeof marketCapSlices)[0];
    pct: number;
  } | null>(null);

  const renderMarketCapPanel = (mobile: boolean) => {
    const R = 15.91549430918954;
    const gap = 0.8;
    let currentOffset = 100;
    const segments: { slice: (typeof marketCapSlices)[0]; dashArray: string; dashOffset: number; pct: number }[] = [];
    marketCapSlices.forEach((slice) => {
      const pct = marketCapTooltipTotal > 0 ? (slice.value / marketCapTooltipTotal) * 100 : 0;
      const fillLength = Math.max(0, pct - gap);
      segments.push({ slice, dashArray: `${fillLength} ${100 - fillLength}`, dashOffset: currentOffset, pct });
      currentOffset -= pct;
    });

    return (
      <Box sx={{ pb: mobile ? 1.25 : 2.5 }}>
        <Box
          sx={{
            width: mobile ? "100%" : 380,
            maxWidth: "100%",
            mx: "auto",
            px: mobile ? "28px" : "28px",
            borderRadius: "12px",
            background: "transparent",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid2 container columnSpacing="12px" sx={{ mb: "28px" }}>
            <Grid2 size={6}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.04)",
                  borderRadius: "12px",
                  p: "14px 16px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  fontFamily="'IBM Plex Mono', monospace"
                  fontWeight={700}
                  fontSize="18px"
                  color="#FFFFFF"
                  lineHeight={1.4}
                >
                  {numberWithCommas(Number(maxSupply.toFixed(0)))}
                </Typography>
                <Typography
                  fontFamily="Inter"
                  fontWeight={600}
                  fontSize="10px"
                  color={KHAKI}
                  lineHeight={1}
                  letterSpacing="0.02em"
                  textTransform="uppercase"
                  mt="4px"
                >
                  Total TR3 Supply
                </Typography>
              </Box>
            </Grid2>
            <Grid2 size={6}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.04)",
                  borderRadius: "12px",
                  p: "14px 16px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography
                  fontFamily="'IBM Plex Mono', monospace"
                  fontWeight={700}
                  fontSize="18px"
                  color="#FFFFFF"
                  lineHeight={1.4}
                >
                  {numberWithCommas(Number(maxMarketCap.toFixed(0)))}{" "}
                  <Typography
                    component="span"
                    fontFamily="'IBM Plex Mono', monospace"
                    fontWeight={600}
                    fontSize="13px"
                    color={KHAKI}
                  >
                    USD
                  </Typography>
                </Typography>
                <Typography
                  fontFamily="Inter"
                  fontWeight={600}
                  fontSize="10px"
                  color={KHAKI}
                  lineHeight={1}
                  letterSpacing="0.02em"
                  textTransform="uppercase"
                  mt="4px"
                >
                  Market Cap
                </Typography>
              </Box>
            </Grid2>
          </Grid2>

          <Box
            sx={{
              height: 220,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: "32px",
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pointerEvents: "none",
                zIndex: 1,
              }}
            >
              <Typography
                fontFamily="'IBM Plex Mono', monospace"
                fontWeight={700}
                fontSize="14px"
                color="#FFFFFF"
                lineHeight={1.3}
              >
                {numberWithCommas(Number(maxSupply.toFixed(0)))} TR3
              </Typography>
              <Typography
                fontFamily="'IBM Plex Mono', monospace"
                fontWeight={700}
                fontSize="10px"
                color={KHAKI}
                textTransform="uppercase"
                letterSpacing="0.1em"
                lineHeight={1.3}
                mt="4px"
              >
                Total
              </Typography>
            </Box>
            <svg
              viewBox="0 0 42 42"
              width={220}
              height={220}
              style={{ transform: "rotate(-90deg)", overflow: "visible" }}
              onMouseLeave={() => {
                setHoveredMarketCapSlice(null);
                setMcTooltip(null);
              }}
            >
              {segments.map(({ slice, dashArray, dashOffset, pct }, i) => (
                <circle
                  key={slice.id}
                  cx="21"
                  cy="21"
                  r={R}
                  fill="none"
                  stroke={slice.color}
                  strokeWidth={hoveredMarketCapSlice === i ? 9 : 6}
                  strokeDasharray={dashArray}
                  strokeDashoffset={dashOffset}
                  style={{
                    cursor: "pointer",
                    transition: "stroke-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s, filter 0.3s",
                    opacity: hoveredMarketCapSlice !== null && hoveredMarketCapSlice !== i ? 0.15 : 1,
                    filter: hoveredMarketCapSlice === i ? `drop-shadow(0 0 12px ${slice.color})` : "none",
                  }}
                  onMouseEnter={(e) => {
                    setHoveredMarketCapSlice(i);
                    setMcTooltip({ x: e.clientX, y: e.clientY, slice, pct });
                  }}
                  onMouseMove={(e) => {
                    setMcTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : null));
                  }}
                  onMouseLeave={() => {
                    setHoveredMarketCapSlice(null);
                    setMcTooltip(null);
                  }}
                />
              ))}
            </svg>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {marketCapSlices.map((slice, i) => (
            <React.Fragment key={slice.id}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  py: "16px",
                  px: mobile ? "28px" : "32px",
                  borderTop: "1px solid rgba(255,255,255,0.08)",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                }}
                onMouseEnter={() => setHoveredMarketCapSlice(i)}
                onMouseLeave={() => setHoveredMarketCapSlice(null)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: slice.color,
                      flexShrink: 0,
                      boxShadow: `0 0 8px ${slice.color}`,
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    fontFamily="Inter"
                    fontWeight={600}
                    fontSize="11px"
                    color="#FFFFFF"
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    {slice.label}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                  <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={600} fontSize="13px" color="#FFFFFF">
                    {numberWithCommas(Number(slice.value.toFixed(0)))}
                  </Typography>
                  <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={600} fontSize="10px" color={KHAKI}>
                    TR3
                  </Typography>
                </Box>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
    );
  };

  const currentEdition = indicators?.halvingCurrentLevel || 1;

  const activeHaYears =
    Number(
      indicators?.netLandSize !== undefined
        ? convertParcelsToHectares(indicators.netLandSize)
        : convertParcelsToHectares(getMaxFromChartData("accumulatedParcels"))
    ) || 0;
  const renderParcelsPanel = (mobile: boolean) => (
    <HalvingMatrixPanel currentEdition={currentEdition} activeHaYears={activeHaYears} mobile={mobile} />
  );

  const getMaxValue = (data: any[]) => {
    const dataKey = type === "parcels" ? "accumulatedParcels" : type;
    const values = data.map((item) => item[dataKey]);
    const maxVal = Math.max(...values) * 1.5;
    if (maxVal > 1) return Math.ceil(maxVal);
    return maxVal;
  };

  const [firstDate, setFirstDate] = useState<Date | null>(null);
  const [lastDate, setLastDate] = useState<Date | null>(null);

  useEffect(() => {
    fetchData();
  }, []);
  useEffect(() => {
    fetchData();
  }, [timeframe, type]);

  const fetchData = async () => {
    let res;
    setIsDataLoaded(false);
    try {
      if (isPieChart) {
        try {
          const indRes = await axios.get(ApiRoutes.ecoBank.indicators.root).then((r) => r.data);
          setIndicators(indRes);
        } catch (e) {
          console.error("Failed to load indicators in line chart", e);
        }
      }

      if (timeframe === "week" || timeframe === "month" || timeframe === "year") {
        const days = timeframe === "week" ? 7 : timeframe === "month" ? 30 : 365;
        res = await axios.get(ApiRoutes.ecoBank.stats(days)).then((r) => r.data);
      } else {
        res = await axios.get(ApiRoutes.ecoBank.allStats).then((r) => r.data);
      }

      const normalizedRes =
        Array.isArray(res) && res.length === 1
          ? [res[0], { ...res[0], createdAt: new Date(new Date(res[0].createdAt).getTime() + 1).toISOString() }]
          : res;

      setChartData(normalizedRes);

      const mapped = normalizedRes
        .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((item: any) => ({
          date: item.createdAt,
          price: Number(item.price),
          supply: Number(item.supply),
          marketCap: Number(item.marketCap),
          treesClaimed: Number(item.treesClaimed),
          parcels: Number(convertParcelsToHectares(item.parcels)),
          accumulatedParcels: Number(convertParcelsToHectares(item.accumulatedParcels)),
        }));
      setCharts(mapped);

      if (mapped.length > 0) {
        setFirstDate(new Date(mapped[0].date));
        setLastDate(new Date(mapped[mapped.length - 1].date));
      }
      setIsDataLoaded(true);
    } catch (error: any) {
      if (error?.name !== "CanceledError" && error?.code !== "ERR_CANCELED") {
        console.error(error);
      }
      setIsDataLoaded(false);
    }
  };

  // ── Tooltip builders ─────────────────────────────────────────────────────
  const buildTooltipContent = (value: any) => {
    const axisType = value?.axis.id;
    const dataIndex = value?.dataIndex;
    const mintData = dataIndex !== null && dataIndex !== undefined ? value?.series[0]?.data[dataIndex] : null;
    const date = moment(value?.axisData?.x?.value).format("DD MMM YYYY");

    switch (axisType) {
      case "price": {
        const mintRate = sixDigits(Number(mintData));
        return (
          <Box sx={hudTooltipSx}>
            <HudLabel sx={{ mb: "12px" }}>{date}</HudLabel>
            <Box display="flex" alignItems="center" gap="6px">
              <HudLabel>TR3 Mint Price:</HudLabel>
              <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={700} fontSize="13px" color={CHART_BLUE}>
                ${mintRate}
              </Typography>
            </Box>
          </Box>
        );
      }

      case "marketCap": {
        const index = value?.axisData?.x?.index;
        const supplyTree = typeof index === "number" && chartData[index] ? chartData[index].supply : "N/A";
        const marketCap = typeof index === "number" && chartData[index] ? chartData[index].marketCap : "N/A";
        const value2 = formatValue(Number(mintData), true, 0, "");
        return (
          <Box sx={hudTooltipSx}>
            <HudLabel sx={{ mb: "12px" }}>{date}</HudLabel>
            <Box display="flex" justifyContent="space-between" width="100%" mb="4px">
              <Box>
                <HudLabel>Market Cap</HudLabel>
                <HudValue>{value2}</HudValue>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <HudLabel>Avg Price/TR3</HudLabel>
                <HudValue>${sixDigits(marketCap / supplyTree)}</HudValue>
              </Box>
            </Box>
          </Box>
        );
      }

      case "supply": {
        const value1 = formatValue(Number(mintData), true, 0, "");
        const index = value?.axisData?.x?.index;
        const treesClaimed = typeof index === "number" && chartData[index] ? chartData[index].treesClaimed : "N/A";
        const guardianRewards =
          typeof index === "number" && chartData[index] ? chartData[index].guardianRedemption : "N/A";
        const patronRewards = typeof index === "number" && chartData[index] ? chartData[index].patronRedemption : "N/A";
        const referrerRewards =
          typeof index === "number" && chartData[index] ? chartData[index].referralRedemption : "N/A";
        const totalMinted = typeof index === "number" && chartData[index] ? chartData[index].mintedTrees : "N/A";
        return (
          <Box sx={hudTooltipSx}>
            <HudLabel sx={{ mb: "12px" }}>{date}</HudLabel>

            <Box display="flex" justifyContent="space-between" width="100%" mb="12px">
              <Box>
                <HudLabel>Supply Cap</HudLabel>
                <HudValue>{value1}</HudValue>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <HudLabel>Total Minted</HudLabel>
                <HudValue>{numberWithCommas(totalMinted)}</HudValue>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", width: "100%", mb: "10px" }} />

            <Stack gap="6px" width="100%" mb="10px">
              <HudRow label="Patron" value={`${numberWithCommas(patronRewards)} TR3/day`} />
              <HudRow label="Guardian" value={`${numberWithCommas(guardianRewards)} TR3/day`} />
              <HudRow label="Referer" value={`${numberWithCommas(referrerRewards)} TR3/day`} />
            </Stack>

            <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", borderStyle: "dashed", width: "100%", mb: "10px" }} />

            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
              <HudLabel>Minted Today</HudLabel>
              <Typography fontFamily="'IBM Plex Mono', monospace" fontWeight={700} fontSize="12px" color={CHART_BLUE}>
                {numberWithCommas(treesClaimed)} TR3
              </Typography>
            </Box>
          </Box>
        );
      }

      case "parcels": {
        const index = value?.axisData?.x?.index;
        const rawAccumulated =
          typeof index === "number" && chartData[index] ? chartData[index].accumulatedParcels : null;
        const rawDaily = typeof index === "number" && chartData[index] ? chartData[index].parcels : null;
        const fmtHa = (v: any) =>
          v !== null ? numberWithCommas(Number(convertParcelsToHectares(v)).toFixed(2)) : "N/A";
        return (
          <Box sx={hudTooltipSx}>
            <HudLabel sx={{ mb: "12px" }}>{date}</HudLabel>
            <Stack gap="8px" width="100%">
              <HudRow label="Total Hectares" value={`${fmtHa(rawAccumulated)} HA`} />
              <HudRow label="Hectares Scanned Daily" value={`${fmtHa(rawDaily)} HA`} />
            </Stack>
          </Box>
        );
      }

      default:
        return (
          <Box sx={hudTooltipSx}>
            <HudLabel>{date}</HudLabel>
          </Box>
        );
    }
  };

  // ── Modal background ─────────────────────────────────────────────────────
  const modalBg = "rgba(16, 18, 16, 0.92)";
  const modalBorder = "1px solid rgba(255,255,255,0.08)";
  const modalShadow = "0 24px 50px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)";

  const pillGroup = (
    <Stack direction="row" spacing={0} gap="8px">
      {["all", "week", "month", "year"].map((tf) => (
        <Pill
          key={tf}
          label={tf.charAt(0).toUpperCase() + tf.slice(1)}
          active={timeframe === tf}
          onClick={() => setTimeframe(tf)}
        />
      ))}
    </Stack>
  );

  const dateRange = (
    <Typography
      fontFamily="'IBM Plex Mono', monospace"
      fontWeight={600}
      fontSize="11px"
      color={KHAKI}
      letterSpacing="0.05em"
      textTransform="uppercase"
    >
      {firstDate ? moment(firstDate).format("MMM Do YYYY") : ""}{" "}
      {lastDate ? `→ ${moment(lastDate).format("MMM Do YYYY")}` : ""}
    </Typography>
  );

  const chartTitle =
    type === "price"
      ? "TR3 Mint Price (USD) Chart"
      : type === "supply"
      ? "TR3 Supply Growth Chart"
      : type === "marketCap"
      ? "TR3 Market Capitalization Chart"
      : type === "treesClaimed"
      ? "TR3 Claimed Chart"
      : type === "parcels"
      ? "Active Hectares Chart"
      : "Average Mint Rate Chart";

  const mobileTitleMap: Record<string, string> = {
    price: "TR3 Mint Rate",
    supply: "TR3 Mint Supply",
    parcels: "Active Hectares Chart",
    marketCap: "TR3 Mint Market Cap",
  };

  const lineChartCommon = {
    colors: [CHART_BLUE],
    dataset: charts,
    xAxis: [
      {
        id: String(type as AxisId),
        dataKey: "date",
        disableLine: true,
        disableTicks: true,
        scaleType: "point" as const,
        valueFormatter: (v: any) => moment(v).format("DD MMM"),
        // Must match the CSS in buildChartSx's `.MuiChartsAxis-tickLabel` override: MUI's
        // built-in tick overlap avoidance measures labels using this style, so if it drifts
        // from the CSS-rendered font/letter-spacing, it under-measures width and lets
        // colliding labels through.
        tickLabelStyle: {
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.05em",
        },
      },
    ],
    yAxis: [
      {
        min: 0,
        max: getMaxValue(charts),
        disableLine: true,
        disableTicks: true,
        label:
          type === "parcels"
            ? "Hectares"
            : type === "supply"
            ? "TR3 Supply"
            : type === "marketCap"
            ? "Market Cap (USD)"
            : "Mint Rate (USD)",
        valueFormatter: (v: number) =>
          formatWithPrefix(v, type === "supply" ? 2 : type === "marketCap" ? 0 : 2, type === "marketCap" ? "$" : ""),
      },
    ],
    series: [
      {
        dataKey: type === "parcels" ? "accumulatedParcels" : type,
        valueFormatter: (v: any) => numberWithCommas(v ?? 0),
        curve: "linear" as const,
        label:
          type === "parcels"
            ? "Parcel"
            : type === "supply"
            ? "Total Supply"
            : type === "marketCap"
            ? "Total Market Cap"
            : "Average Mint Rate",
        showMark:
          timeframe === "week"
            ? true
            : timeframe === "month"
            ? ({ index }: { index: number }) => index % 4 === 0
            : false,
        area: true,
      },
    ],
    slotProps: { legend: { hidden: true } },
    height: 300,
    grid: { vertical: false, horizontal: true },
    tooltip: isDataLoaded
      ? { trigger: "axis" as const, axisContent: buildTooltipContent }
      : { trigger: "none" as const },
  };

  const mcTooltipPortal = mcTooltip
    ? createPortal(
        <div
          style={{
            position: "fixed",
            left: mcTooltip.x,
            top: mcTooltip.y,
            transform: "translate(-50%, calc(-100% - 12px))",
            pointerEvents: "none",
            zIndex: 9999,
            backgroundColor: "rgba(14, 16, 14, 0.98)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            padding: "10px 14px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.8)",
            backdropFilter: "blur(10px)",
            whiteSpace: "nowrap",
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          <div
            style={{
              fontSize: "9px",
              fontWeight: 700,
              color: mcTooltip.slice.color,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: "4px",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {mcTooltip.slice.label}
          </div>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#FFFFFF" }}>
            {numberWithCommas(Number(mcTooltip.slice.value.toFixed(0)))} TR3 ({mcTooltip.pct.toFixed(1)}%)
          </div>
        </div>,
        document.body
      )
    : null;

  // ── Desktop ──────────────────────────────────────────────────────────────
  if (!isMobile) {
    return (
      <React.Fragment>
        {mcTooltipPortal}
        <Box
          sx={{
            width: isPieChart ? "400px" : "720px",
            margin: "auto",
            padding: "32px 0 0 0",
            backgroundColor: modalBg,
            border: modalBorder,
            borderRadius: "20px",
            boxShadow: modalShadow,
            backdropFilter: "blur(20px)",
            ...(!isPieChart && {
              "& svg": { height: "285px !important", position: "absolute", left: "4px", bottom: "50px" },
            }),
          }}
        >
          <Box sx={{ padding: isPieChart ? "0 20px" : "0 40px" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="24px">
              <Typography
                fontFamily="'Inter', -apple-system, BlinkMacSystemFont, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'"
                fontWeight={600}
                fontSize="16px"
                letterSpacing="0.02em"
                color="#FFFFFF"
              >
                {chartTitle}
              </Typography>
              <IconButton disableFocusRipple disableRipple size="small" onClick={onClose}>
                <Typography
                  fontFamily="Inter"
                  fontSize="12px"
                  color={KHAKI}
                  sx={{
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                    "&:hover": { color: "#FFFFFF" },
                    transition: "color 0.2s",
                  }}
                >
                  Close
                </Typography>
              </IconButton>
            </Box>

            {!isPieChart && (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb="48px">
                {pillGroup}
                {dateRange}
              </Box>
            )}
          </Box>

          {isMarketCap ? (
            renderMarketCapPanel(false)
          ) : isParcels ? (
            renderParcelsPanel(false)
          ) : (
            <LineChart {...lineChartCommon} sx={buildChartSx(gradientId)}>
              <AreaGradient color={CHART_BLUE} id={gradientId} />
            </LineChart>
          )}
        </Box>
      </React.Fragment>
    );
  }

  // ── Mobile ───────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      {mcTooltipPortal}
      <Box
        {...rest}
        ref={forwardedRef}
        sx={{
          width: "100%",
          margin: "auto",
          padding: isPieChart ? "18px 0" : "30px 0",
          backgroundColor: modalBg,
          borderRadius: "20px 20px 0 0",
          border: modalBorder,
          boxShadow: modalShadow,
          backdropFilter: "blur(20px)",
        }}
      >
        <Box sx={{ padding: "0 20px" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
            <Typography
              fontFamily="'IBM Plex Mono', monospace"
              fontWeight={600}
              fontSize="14px"
              letterSpacing="0.02em"
              color="#FFFFFF"
            >
              {mobileTitleMap[type] ?? chartTitle}
            </Typography>
            <IconButton disableFocusRipple disableRipple size="small" onClick={onClose}>
              <Typography
                fontFamily="Inter"
                fontSize="12px"
                color={KHAKI}
                sx={{
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                  position: "relative",
                  bottom: "50px",
                  "&:hover": { color: "#FFFFFF" },
                  transition: "color 0.2s",
                }}
              >
                Close
              </Typography>
            </IconButton>
          </Box>

          {!isPieChart && (
            <>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                {pillGroup}
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb="20px">
                <Typography
                  fontFamily="'IBM Plex Mono', monospace"
                  fontWeight={600}
                  fontSize="11px"
                  color={KHAKI}
                  letterSpacing="0.05em"
                  textTransform="uppercase"
                >
                  {firstDate ? moment(firstDate).format("MMM Do YYYY") : ""}
                </Typography>
                <Typography
                  fontFamily="'IBM Plex Mono', monospace"
                  fontWeight={600}
                  fontSize="11px"
                  color={KHAKI}
                  letterSpacing="0.05em"
                  textTransform="uppercase"
                  textAlign="end"
                >
                  {lastDate ? moment(lastDate).format("MMM Do YYYY") : ""}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {isMarketCap ? (
          renderMarketCapPanel(true)
        ) : isParcels ? (
          renderParcelsPanel(true)
        ) : (
          <LineChart
            {...lineChartCommon}
            margin={{ left: 70, right: 16, top: 10, bottom: 40 }}
            sx={buildChartSx(`chart-gradient-mobile`)}
          >
            <AreaGradient color={CHART_BLUE} id="chart-gradient-mobile" />
          </LineChart>
        )}
      </Box>
    </React.Fragment>
  );
}

const PopUpGraph = React.forwardRef<HTMLDivElement, GridDemoProps & PopUpGraphDomProps>((props, ref) => (
  <PopUpGraphInner {...props} forwardedRef={ref} />
));
PopUpGraph.displayName = "PopUpGraph";

export default PopUpGraph;
